// ============================================================================
// Core domain types. The Card discriminated union is the keystone: a Driver and
// a Chassis have different fields, and the `type` tag is the discriminant. You
// cannot build a Chassis out of a Driver row without the compiler rejecting it,
// which is exactly the class of bug that produced "MOT Ferrari / AER mythic".
// ============================================================================

export type Rarity = "normal" | "legend" | "mythic";

export type Country =
  | "BRA" | "GER" | "GBR" | "NED" | "FRA" | "AUT" | "ESP" | "FIN"
  | "AUS" | "MEX" | "ITA" | "CAN" | "COL" | "ARG" | "SWE" | "JPN"
  | "MON" | "THA";

export type Constructor =
  | "Ferrari" | "McLaren" | "Mercedes" | "Red Bull" | "Williams"
  | "Brawn" | "Renault" | "Benetton" | "Lotus";

export interface Driver {
  readonly type: "driver";
  readonly name: string;
  readonly country: Country;
  readonly team: Constructor;  // the stint this card represents; also the variant key (name|team)
  readonly rarity: Rarity;
  readonly pace: number;
  readonly consistency: number;
  readonly rain: number;
}

export interface Chassis {
  readonly type: "chassis";
  readonly team: Constructor;
  readonly model: string;      // e.g. "MP4/4 1988"
  readonly rarity: Rarity;
  readonly motor: number;
  readonly aero: number;
}

export type Card = Driver | Chassis;

// Type guards — narrow a Card to its concrete variant.
export const isDriver = (c: Card): c is Driver => c.type === "driver";
export const isChassis = (c: Card): c is Chassis => c.type === "chassis";

// ----------------------------------------------------------------------------
// Director (Diretor de Equipe)
// ----------------------------------------------------------------------------

// The 4th draft pick: a team principal that modifies the whole lineup. Unlike
// Driver/Chassis it touches levers nothing else does — DNF/reliability, luck
// variance, season-long development — plus it can amplify existing synergy.
//
// NOT yet part of the `Card` union: it joins the draft model (Slots, packs,
// persistence, rivals) in a later step, which is why exhaustive switches over
// Card stay untouched for now. See docs/ROADMAP.md.
//
// `signature` is the headline, often situational effect. It is structured (a
// `kind` plus params), exactly like SynergyReason below, so the UI renders it
// in any language at display time without ever storing a translated string.
export type DirectorTrait =
  | { readonly kind: "strategist"; readonly paceBonus: number }   // race pace — scales with the track's pace weight
  | { readonly kind: "qualifier"; readonly gridBonus: number }    // flat grid-position score, UNDILUTED by track pace weight (shines on low-pace circuits)
  | { readonly kind: "engineer"; readonly dnfCut: number }        // cuts DNF risk in all weather, extra in the wet
  | { readonly kind: "motivator"; readonly synergyMult: number }  // amplifies existing synergy
  | { readonly kind: "gambler"; readonly luckMult: number; readonly scoreBonus: number } // high risk / high reward
  | { readonly kind: "rainMaster"; readonly wetBonus: number }    // wet-weather edge
  | { readonly kind: "developer"; readonly rampMax: number }      // bonus that grows across the season
  | { readonly kind: "underdog"; readonly synergyMult: number; readonly maxMythics: number }; // synergy boost only with a cheap roster

export interface Director {
  readonly type: "director";
  readonly name: string;
  // Historic constructor for affinity, mirroring Driver.team / HIST_TEAM. `null`
  // for directors with no constructor in the pool (e.g. Haas) or for unlicensed
  // generics — they simply get no historic affinity.
  readonly team: Constructor | null;
  readonly rarity: Rarity;
  // Two generic stats every director has, on top of the signature:
  readonly devSlope: number;     // per-race ramp (points-equivalent added per round; 0 = flat)
  readonly reliability: number;  // dampens luck variance; NEGATIVE for aggressive directors who trade reliability for pace
  readonly signature: DirectorTrait;
}

export const isDirector = (c: { readonly type: string }): c is Director =>
  c.type === "director";

// ----------------------------------------------------------------------------
// Synergy
// ----------------------------------------------------------------------------

// Synergy reasons are stored as language-neutral structured data (a `kind` plus
// proper-noun params) so the UI can render them in any language at display time
// — including re-translating a season that was drafted before a language switch.
export type SynergyReason =
  | { readonly kind: "historicDrive"; readonly driver: string; readonly team: Constructor }
  | { readonly kind: "sameNation"; readonly country: Country }
  | { readonly kind: "sharedTeam"; readonly a: string; readonly b: string };

// Team-wide synergy: only the genuinely shared, pairwise bonuses live here
// (same-nation consistency, shared-team motor). The historic-drive bonus is
// per-driver — it boosts only the driver who actually raced that chassis — so
// it is NOT here; see `historicAffinity` in engine/synergy.ts.
export interface SynergyBonus {
  readonly consistency: number;
  readonly motor: number;
  readonly reasons: readonly SynergyReason[];
}

// Per-driver bonus from having raced for the chassis's constructor. Zero for a
// driver — including the teammate — who never drove that chassis. Both halves are
// DRIVER attributes (familiarity → faster and more dialed-in); the car's own
// stats (motor/aero) are the chassis's and the director's domain, not a driver's.
export interface DriverAffinity {
  readonly pace: number;
  readonly consistency: number;
}

// A single synergy tag shown on a draft card (candidate vs already-picked).
// Structured for the same reason as SynergyReason.
export type SynergyTag =
  | { readonly kind: "droveFor"; readonly team: Constructor }
  | { readonly kind: "sameNationAs"; readonly driver: string; readonly country: Country }
  | { readonly kind: "exTeammate"; readonly driver: string }
  | { readonly kind: "chassisDroveFor"; readonly driver: string; readonly team: Constructor };

// ----------------------------------------------------------------------------
// Teams
// ----------------------------------------------------------------------------

export interface Team {
  readonly name: string;
  readonly d1: Driver;
  readonly d2: Driver;
  readonly chassis: Chassis;
  // The team principal. `null` for rivals until each fixed lineup gets a thematic
  // director, and a forward-compatible default for seasons drafted before the
  // director slot existed. baseScore does not read it yet — that's a later step.
  readonly director: Director | null;
  readonly synergy: SynergyBonus;
  readonly isPlayer: boolean;
  points: number; // mutated during a season
}

// A rival lineup is defined by stable keys resolved against the pools at build
// time. DriverKey is `${name}|${team}`; ChassisKey is the model string.
export type DriverKey = string;
export type ChassisKey = string;
export type DirectorKey = string; // a director's name (unique within the pool)

export interface RivalDef {
  readonly name: string;
  readonly d1: DriverKey;
  readonly d2: DriverKey;
  readonly chassis: ChassisKey;
  readonly director: DirectorKey; // the team's historic principal (resolved at build time)
}

// ----------------------------------------------------------------------------
// Tracks & racing
// ----------------------------------------------------------------------------

export type Weather = "dry" | "light_rain" | "heavy_rain";

export interface TrackWeights {
  readonly teamMotor: number;
  readonly teamAero: number;
  readonly driverPace: number;
  readonly driverConsistency: number;
}

export interface Track {
  readonly name: string;
  readonly label: string;
  readonly weights: TrackWeights;
}

export interface RaceEntry {
  readonly teamName: string;
  readonly isPlayer: boolean;
  readonly driverName: string;
  readonly driverTeam: Constructor; // the driver's stint/variant (name|team), for display + dedup
  readonly position: number | null; // null = DNF
  readonly points: number;
  readonly dnf: boolean;
}

export interface RaceResult {
  readonly track: Track;
  readonly weather: Weather;
  readonly order: readonly RaceEntry[];
}

// ----------------------------------------------------------------------------
// Draft & season state
// ----------------------------------------------------------------------------

// A per-card reroll keeps one trait of a card and redraws the rest of the pack.
// "team" applies to drivers and chassis; "country" only to drivers.
export type RerollKeep = "team" | "country";

// One player action during the draft, logged in order for a Daily run so the
// server can replay the seed and recompute the score (cheat-resistant
// leaderboard). The log is tiny — a draft is ~6–10 actions. See engine/replay.ts.
export type Decision =
  | { readonly t: "pick"; readonly i: number }       // keep pack card at index i
  | { readonly t: "reroll" }                          // reroll the whole card pack
  | { readonly t: "rerollKeep"; readonly i: number; readonly keep: RerollKeep } // locked reroll
  | { readonly t: "pickDir"; readonly i: number }     // keep director at index i (finalizes)
  | { readonly t: "rerollDir" };                      // reroll the director pack

export interface Slots {
  d1: Driver | null;
  d2: Driver | null;
  chassis: Chassis | null;
  director: Director | null; // the 4th pick, filled last (after d1/d2/chassis)
}

export interface DriverStanding {
  readonly driverName: string;
  readonly driverTeam: Constructor;
  readonly teamName: string;
  readonly isPlayer: boolean;
  readonly points: number;
}

export interface Standings {
  readonly constructors: readonly Team[];
  readonly drivers: readonly DriverStanding[];
}

export type Phase = "menu" | "naming" | "draft" | "season" | "result";

export interface SeasonState {
  teamName: string;
  chaos: boolean;       // high luck swings during races when on
  fromMemory: boolean;  // hide card attributes during the draft
  seed: number;         // RNG seed this season was drawn from (random, or date-derived for daily)
  daily: boolean;       // true for a Daily Challenge run (modifiers locked, seed = today)
  day: string;          // YYYY-MM-DD key for a daily run; "" otherwise
  rngDraws: number;     // draws taken from the season RNG, so a reload resumes the same sequence
  picked: Card[];
  mythicsPicked: number;
  rollNo: number;
  currentPremium: boolean;     // was the current pack a premium pack?
  currentChance: number;       // premium chance used for the current roll
  premChance: number;          // running premium chance for the next roll
  pack: Card[];
  // The final, director-only pack. Empty until the 2 drivers + chassis are in;
  // a dedicated pool (not the Card `pack` above) so the director phase never
  // touches the driver/chassis draw or premium-pity machinery.
  directorPack: Director[];
  rerollLeft: number;
  slots: Slots;
  calendar: Track[];
  weather: Weather[];   // pre-drawn weather per round, fixed by the seed (shown in the draft)
  round: number;
  results: RaceResult[];
  opponents: Team[];
  standings: Standings | null;
  // Ordered log of the player's draft actions, recorded only for a Daily run so
  // the server can replay the seed and verify the leaderboard score. Absent on
  // free seasons (and on dailies drafted before this field existed).
  decisionLog?: Decision[];
}

export interface HistoryEntry {
  readonly wcc: string;
  readonly wdc: string;
  readonly playerWonWcc: boolean;
  readonly date: number;
}

// ----------------------------------------------------------------------------
// Badges & challenges
// ----------------------------------------------------------------------------

// Permanent, account-wide unlocks. The catalog (names, descriptions, and the
// progress check against a SeasonState) lives in data/badges.ts — only the
// id is persisted, so renaming a badge's copy never invalidates a save.
export type BadgeId =
  | "wcc_champion"
  | "wdc_champion"
  | "chaos_champion"
  | "memory_champion"
  | "no_mythic_champion"
  | "rain_master"
  | "perfect_lineup"
  | "grand_slam"
  | "king_of_the_midfield"
  | "one_two"
  | "total_dominance"
  | "unbreakable"
  | "national_pride"
  | "late_charge"
  | "pace_master"
  | "perfect_harmony"
  | "goat"
  // Nation challenges: win the Constructors' title with two drivers of one
  // nation. Ordered easy → hard by the best legal pairing the roster allows
  // (no duplicate driver identities — see draft.ts). COL and NED are omitted:
  // a single card in the roster makes a same-nation pair impossible.
  | "nation_bra"
  | "nation_gbr"
  | "nation_ger"
  | "nation_fra"
  | "nation_arg"
  | "nation_esp"
  | "nation_fin"
  | "nation_aus"
  | "nation_aut"
  | "nation_ita"
  | "nation_mex"
  | "nation_can";

// Sections the Badges page groups its catalog under, rendered in this order.
export type BadgeGroup = "championship" | "mastery" | "nation";

export interface GameState {
  phase: Phase;
  season: SeasonState | null;
  history: HistoryEntry[];
  unlockedBadges: BadgeId[];
  // The player's team name, remembered across seasons (editable from the menu).
  // "" means none set yet — the draft falls back to a default.
  teamName: string;
  // The finished Daily Challenge season for the day it was played, kept so the
  // menu can show the result and a retry jumps straight to it instead of
  // replaying. Superseded when a new day's daily is completed.
  dailySeason?: SeasonState | null;
  // The YYYY-MM-DD of the last daily successfully posted to the leaderboard, so
  // a result re-render or reload doesn't double-submit. Absent until the first
  // successful submission.
  lastSubmittedDay?: string;
  // The YYYY-MM-DD of a Daily Challenge the player forfeited (abandoned or hit
  // "start over" mid-run). One attempt per day, so a failed day is not re-offered.
  dailyFailedDay?: string;
}
